<template>
  <!-- Replacement Quote Edit Summary page: the hosted quotebuilder/edit form (Frontend\QuoteBuilderController@edit/@update)
       in the app theme — Summary Title, Title, Total (read-only) and the title of every quote line (restricted lines locked).
       Data: GET orders/summary/edit-data, save: POST orders/summary/update (G-78, on ado-7600; save rebuilds the stored
       summary). If the server refuses (or doesn't have the route yet) the card says so. -->
  <div class="w-full font-roboto">
    <div
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton :fallback="summaryPath" />
      <div
        class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px]">
        <template v-for="(crumb, index) in crumbs" :key="crumb.label">
          <span v-if="index > 0" class="text-[#CCCCCC]">|</span>
          <a v-if="crumb.to" :href="$router.resolve(crumb.to).href" class="no-underline text-[#8479E1]"
            @click="openCrumb($event, crumb.to)">{{ crumb.label }}</a>
          <span v-else class="text-[#000000]">{{ crumb.label }}</span>
        </template>
      </div>

      <div v-if="loading" class="fxShimmer h-[300px] rounded-[20px]" />

      <!-- native form -->
      <div v-else-if="form" class="rqCard !p-0 overflow-hidden">
        <div class="px-[15px] laptop:px-[20px] py-[12px] bg-[#8479E1]">
          <p class="m-0 text-[16px] font-medium text-white">Edit Summary</p>
        </div>
        <div class="px-[15px] laptop:px-[20px] py-[18px]">
          <div class="grid tablet:grid-cols-2 laptop:grid-cols-3 gap-[12px]">
            <div>
              <label class="uqcLabel" for="rqSumTitle">Summary Title</label>
              <inputComponent ref="summaryTitle" v-model="form.order_summary_title" name="rqSumTitle" required
                required-message="Summary title is required" :custom-class="fieldClass" />
            </div>
            <div>
              <label class="uqcLabel" for="rqTierTitle">Title</label>
              <inputComponent ref="tierTitle" v-model="form.tier_title" name="rqTierTitle" required
                required-message="Title is required" :custom-class="fieldClass" />
            </div>
            <div>
              <label class="uqcLabel" for="rqSumTotal">Total</label>
              <inputComponent :model-value="form.total_text" name="rqSumTotal" disabled :custom-class="fieldClass" />
            </div>
          </div>

          <!-- one section per line-item group, each line: Title (editable unless restricted), Cash / Retail Price (read-only) -->
          <section v-for="group in form.groups" :key="group.key" class="mt-[22px] pt-[18px] border-t-1 border-[#EFEFEF]">
            <div class="flex items-center gap-1 pb-[12px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[17px] laptop:text-[18px] font-semibold pl-2 m-0 text-[#333333]">{{ group.heading }}</p>
            </div>
            <div class="flex flex-col">
              <div v-for="item in group.items" :key="item.id"
                class="grid tablet:grid-cols-[2fr_1fr_1fr] gap-[12px] py-[12px] border-b-1 border-[#F3F3F3] last:border-b-0">
                <div>
                  <label class="uqcLabel" :for="'rqLine' + item.id">Title</label>
                  <inputComponent :ref="'line' + item.id" v-model="titles[item.id]" :name="'rqLine' + item.id"
                    :disabled="!item.editable" required required-message="Title is required" :custom-class="fieldClass" />
                </div>
                <div>
                  <label class="uqcLabel">Cash Price</label>
                  <inputComponent :model-value="money(item.price)" disabled :custom-class="fieldClass" />
                </div>
                <div>
                  <label class="uqcLabel">Retail Price</label>
                  <inputComponent :model-value="money(item.retail_price)" disabled :custom-class="fieldClass" />
                </div>
              </div>
            </div>
          </section>
        </div>
        <div class="flex items-center justify-end gap-[10px] px-[15px] laptop:px-[20px] py-[12px] border-t-1 border-[#EFEFEF]">
          <button type="button" class="uqcBtnCancel" @click="toSummary">Cancel</button>
          <button type="button" class="uqcBtnNext" :disabled="saving" @click="save">Save</button>
        </div>
      </div>

      <!-- the API isn't there yet (G-78) -->
      <div v-else class="rqCard !p-0 overflow-hidden">
        <div class="px-[15px] laptop:px-[20px] py-[12px] bg-[#8479E1]">
          <p class="m-0 text-[16px] font-medium text-white">Edit Summary</p>
        </div>
        <div class="flex flex-col items-center gap-[10px] px-[15px] py-[40px] text-center">
          <span class="w-[48px] h-[48px] rounded-full flex items-center justify-center bg-[#F3F0FF]">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="#8479E1" stroke-width="2" />
              <path d="M12 11v5M12 8h.01" stroke="#8479E1" stroke-width="2.2" stroke-linecap="round" />
            </svg>
          </span>
          <p class="m-0 text-[15px] font-medium text-[#333333]">Editing the summary isn't available yet.</p>
          <p class="m-0 text-[13px] text-[#666666]">{{ unavailableReason }}</p>
          <button type="button" class="uqcBtnNext mt-[6px]" @click="toSummary">Back to Summary</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import inputComponent from "@/components/partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";
import { isSilentLogin } from "@/helpers/embedMode";
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";
const toast = useToast();

export default {
  components: { DetailBackButton, inputComponent },
  data() {
    return {
      loading: true,
      saving: false,
      // { order_summary_title, tier_title, total_text, groups: [{ key, heading, items: [{ id, title, editable, price, retail_price }] }] }
      form: null,
      titles: {},
      unavailableReason: "",
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333] disabled:!bg-[#F7F8FA] disabled:cursor-not-allowed",
    };
  },
  computed: {
    orderId() {
      return this.$route.params.id;
    },
    detailPath() {
      return `/replacement-quote/detail/${this.orderId}`;
    },
    // opened from Admin Utilities: keep ?from=admin so the summary still offers Edit Summary
    summaryPath() {
      return `/replacement-quote/summary/${this.orderId}` + (this.$route.query.from === "admin" ? "?from=admin" : "");
    },
    crumbs() {
      if (this.$route.query.from === "admin") {
        return [{ label: "Admin Utilities", to: "/admin-utilities" }, { label: "System Summary", to: this.summaryPath }, { label: "Edit Summary" }];
      }
      return [
        ...(isSilentLogin() ? [] : [{ label: "Replacement Quote History", to: "/work-order/replacement-quote" }]),
        { label: "Order Details", to: this.detailPath },
        { label: "System Summary", to: this.summaryPath },
        { label: "Edit Summary" },
      ];
    },
  },
  watch: {
    orderId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      this.loading = true;
      this.form = null;
      const res = await rqApi.summaryEditData(this.orderId);
      if (res.ok && res.body) {
        this.form = { ...res.body, groups: res.body.groups || [] };
        this.titles = Object.fromEntries(this.form.groups.flatMap(g => g.items).map(i => [i.id, i.title || ""]));
      } else {
        // a server without the route (QA until the G-78 deploy) answers "UNTRACKED: The route … could not be found"
        this.unavailableReason = /UNTRACKED|could not be found/i.test(res.message || "") ? "" : res.message;
      }
      this.loading = false;
    },
    money(n) {
      return (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
    async save() {
      const refs = [this.$refs.summaryTitle, this.$refs.tierTitle,
        ...this.form.groups.flatMap(g => g.items).filter(i => i.editable).map(i => [].concat(this.$refs["line" + i.id])[0])];
      // validate() returns the error text ('' = valid)
      if (refs.filter(Boolean).map(r => r.validate()).some(Boolean)) return;
      // only the lines whose title changed, like the hosted form (titles vs old_titles)
      const lines = this.form.groups.flatMap(g => g.items).filter(i => i.editable && this.titles[i.id] !== i.title);
      this.saving = true;
      const res = await rqApi.summaryUpdate(this.orderId, {
        order_summary_title: this.form.order_summary_title.trim(),
        tier_title: this.form.tier_title.trim(),
        titles: Object.fromEntries(lines.map(i => [i.id, this.titles[i.id].trim()])),
      });
      this.saving = false;
      if (!res.ok) {
        toast.error(res.message || "Unable to update the summary.");
        return;
      }
      toast.success(res.message || "Summary has been updated successfully!");
      this.toSummary();
    },
    openCrumb(event, to) {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      goBackTo(this.$router, to);
    },
    toSummary() {
      goBackTo(this.$router, this.summaryPath);
    },
  },
};
</script>
